import * as Sentry from "@sentry/react"

import { ChartContext } from "@tokenterminal/core/feature/chart/tt-ui-chart/ChartContext"
import { useTracking } from "components/Tracking"
import { sendEvent } from "modules/analytics"
import {
  ACCESS_ACTIONS,
  ACCESS_COMPONENTS,
  useAccess,
} from "modules/auth/useAccess"
import { downloadChartDataBlob } from "modules/chart/ChartDownload/downloadChartData"
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useContext,
} from "react"

const useChartDownloadAction = ({
  setIsProPaywallModalOpen,
  title,
  filters,
}: {
  setIsProPaywallModalOpen: Dispatch<SetStateAction<boolean>>
  title: string
  filters?: Record<string, any>
}) => {
  const { getChartCSV } = useContext(ChartContext)
  const { contextValue } = useTracking()
  const canDownload = useAccess(
    ACCESS_COMPONENTS.STUDIO,
    ACCESS_ACTIONS.DOWNLOAD
  )

  const downloadChart = useCallback(async () => {
    if (!canDownload) {
      setIsProPaywallModalOpen(true)

      sendEvent("wall", {
        action: "download",
        wall_type: "pro",
        component: "chart",
        name: title,
        filters: filters ?? {},
        page_id: contextValue.pageId!,
        page_type: "studio",
      })

      return
    }

    sendEvent("download", {
      component: "chart",
      format: "csv",
      name: title,
      filters: filters ?? {},
      page_id: contextValue.pageId!,
      page_type: "studio",
    })

    const csv = getChartCSV()

    if (csv) {
      downloadChartDataBlob(title, csv)
    } else {
      Sentry.captureException(new Error("Failed to download chart data"), {
        extra: {
          component: "studio",
        },
      })
    }
    // We don't need to pass the setIsProPaywallOpen to the callback
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canDownload, contextValue.pageId, getChartCSV])

  return {
    downloadChart,
  }
}

export default useChartDownloadAction
